import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShoppingBag, 
  Mail, 
  MessageSquare, 
  Search, 
  Globe, 
  Settings, 
  Zap, 
  Trash2,
  ExternalLink
} from 'lucide-react';

const CapabilityIcon = ({ capability }) => {
  const icons = {
    ecommerce: ShoppingBag,
    email: Mail,
    social: MessageSquare,
    seo: Search,
    cms: Globe,
    automation: Zap
  };
  const Icon = icons[capability] || Settings;
  return (
    <div className="p-1.5 bg-gray-100 rounded-md" title={capability}>
      <Icon className="h-3.5 w-3.5 text-gray-500" />
    </div>
  );
};

const IntegrationCard = ({ integration, onConnect, onManage, onAutomation, onDisconnect }) => {
  const { id, name, description, logo, status, category, capabilities, account } = integration;
  const isConnected = status === 'connected';
  
  // Extract details from nested account if available
  const acct = account?.account || {};
  const profilePictureUrl = acct.profile_picture_url || '';
  const instagramName = acct.instagram_name || '';
  const instagramUsername = acct.instagram_username || '';
  const overallStatus = account?.overall_status;
  const needsReconnection = overallStatus === 'token_expired' || overallStatus === 'disconnected';

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`relative p-5 sm:p-6 rounded-[1.5rem] flex flex-col h-full transition-all duration-300 border ${
        isConnected 
          ? 'bg-white border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1' 
          : 'bg-gray-50/50 border-gray-200 hover:bg-white hover:shadow-xl'
      }`}
    >
      {/* Top row: Logo and Status */}
      <div className="flex justify-between items-start mb-4">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden border border-gray-100 shadow-sm p-1.5">
          {logo ? (
            <img src={logo} alt={name} className="w-full h-full object-contain" />
          ) : (
            <Globe className="h-5 w-5 text-gray-400" />
          )}
        </div>
        <div className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 border ${
          isConnected && !needsReconnection
            ? 'bg-green-50 text-green-600 border-green-100' 
            : isConnected && needsReconnection
            ? 'bg-red-50 text-red-600 border-red-100'
            : 'bg-white text-gray-400 border-gray-200'
        }`}>
          {isConnected && !needsReconnection && <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />}
          {isConnected && needsReconnection && <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
          {isConnected && !needsReconnection ? 'Connected' : isConnected && needsReconnection ? (overallStatus === 'token_expired' ? 'Token Expired' : 'Disconnected') : 'Available'}
        </div>
      </div>

      {/* Middle: Content */}
      <div className="mb-6 flex-1">
        {isConnected && account ? (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-black text-gray-900 tracking-tight">{name}</h3>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">Active Account</p>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-gray-50/50 border border-gray-100 rounded-xl group transition-colors hover:bg-gray-50">
              {profilePictureUrl ? (
                <img src={profilePictureUrl} alt={instagramUsername} className="w-10 h-10 rounded-lg border border-white shadow-sm object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gradient-to-tr from-gray-700 via-gray-900 to-black flex items-center justify-center text-white font-black text-base shadow-sm">
                  {instagramName ? instagramName.charAt(0).toUpperCase() : 'I'}
                </div>
              )}
              <div className="overflow-hidden">
                <p className="text-sm font-bold text-gray-900 truncate">{instagramName || 'Instagram User'}</p>
                {instagramUsername && <p className="text-xs text-gray-500 truncate font-medium">@{instagramUsername}</p>}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="text-xl font-black text-gray-900 tracking-tight">{name}</h3>
            <p className="text-sm text-gray-500 leading-relaxed font-medium">
              {description}
            </p>
          </div>
        )}
      </div>

      {/* Bottom: Action Buttons */}
      <div className="mt-auto pt-5 border-t border-gray-100 flex items-center gap-2">
        {isConnected && !needsReconnection ? (
          <>
            <button 
              onClick={() => onManage(id)}
              className="flex-1 px-4 py-2.5 bg-black hover:bg-gray-800 text-white text-[11px] font-bold rounded-xl transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Manage
            </button>
            <div className="flex gap-1.5">
              {/* <button 
                onClick={() => onAutomation(id)}
                className="p-2.5 bg-purple-50 hover:bg-purple-100 text-purple-600 rounded-xl transition-all border border-purple-100 flex items-center justify-center group"
                title="Automations"
              >
                <Zap className="h-4 w-4 group-hover:scale-110 transition-transform" />
              </button> */}
              <button 
                onClick={() => onDisconnect(id)}
                className="p-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-all border border-red-100 flex items-center justify-center group"
                title="Disconnect"
              >
                <Trash2 className="h-4 w-4 group-hover:scale-110 transition-transform" />
              </button>
            </div>
          </>
        ) : isConnected && needsReconnection ? (
          <button 
            onClick={() => onManage(id)}
            className="w-full px-4 py-3 bg-red-50 border border-red-100 text-red-600 hover:bg-red-100 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2"
          >
            <Settings className="h-4 w-4" />
            Fix Connection
          </button>
        ) : (
          <button 
            onClick={() => onConnect(integration)}
            className="w-full px-4 py-3 bg-black text-white hover:bg-gray-800 text-xs font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
          >
            Connect {name}
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default IntegrationCard;
